import io from "socket.io-client";

const SOCKET_ENDPOINT = process.env.REACT_APP_BACKEND_URL;

let socketInstance = null;

/**
 * Create socket connection with JWT auth. Call when user is logged in (e.g. from App when access_token is set).
 * Disconnects any existing connection before creating a new one.
 * @param {string} accessToken - JWT access token for handshake auth
 * @returns {import("socket.io-client").Socket}
 */
export function initSocket(accessToken) {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
  if (!accessToken || !SOCKET_ENDPOINT) return null;
  socketInstance = io(SOCKET_ENDPOINT, {
    auth: { token: accessToken },
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: Infinity,
  });

  socketInstance.on("connect", () => {});

  socketInstance.on("disconnect", () => {});

  socketInstance.on("connect_error", (error) => {
    console.error("[Socket] Connection error:", error);
  });

  return socketInstance;
}

/**
 * Disconnect and clear socket. Call on logout.
 */
export function disconnectSocket() {
  if (socketInstance) {
    socketInstance.disconnect();
    socketInstance = null;
  }
}

/** Raw socket for advanced listeners (e.g. scraper events). Null if not connected. */
export function getSocketInstance() {
  return socketInstance;
}

/**
 * Proxy object that delegates to the current socket instance.
 * Use this so components can import { socket } and call socket.emit / socket.on;
 * once initSocket(token) has been called from App, the real connection is used.
 */
export const socket = new Proxy(
  {},
  {
    get(_, prop) {
      if (socketInstance && typeof socketInstance[prop] === "function") {
        return socketInstance[prop].bind(socketInstance);
      }
      if (socketInstance && prop in socketInstance) {
        return socketInstance[prop];
      }
      if (prop === "connected") return socketInstance?.connected ?? false;
      if (prop === "id") return socketInstance?.id ?? null;

      // Prevent crash if socket is accessed before initSocket()
      if (!socketInstance && ["on", "off", "emit", "once", "removeListener", "connect", "disconnect"].includes(prop)) {
        return () => {};
      }

      return undefined;
    },
  }
);
